import React from "react";
import { Link } from "react-router-dom";
import { Search, X, Users, Star, MessageCircle, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { RANKING_COLORS } from "../admin/constants";

const ProfessorStudents = ({ students, loading, total, page, pages, searchQuery, onSearchChange, onPageChange, onOpenRankingDialog, onOpenFeedbackDialog }) => {
  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
        <Input
          placeholder="Pesquisar por nome, email ou telefone..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
          data-testid="prof-student-search"
        />
        {searchQuery && (
          <button onClick={() => onSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-10 h-10 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : students.length === 0 ? (
        <Card className="bg-white border-0 shadow-sm rounded-xl">
          <CardContent className="p-12 text-center">
            <Users className="h-10 w-10 text-[#94A3B8] mx-auto mb-3" />
            <p className="text-[#475569]">{searchQuery ? "Nenhum aluno encontrado" : "Sem alunos registados"}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-xs text-[#94A3B8]">{total} aluno{total !== 1 ? "s" : ""}</p>
          <div className="grid gap-3">
            {students.map((student) => (
              <Card key={student.user_id} className="bg-white border-0 shadow-sm rounded-xl" data-testid={`prof-student-${student.user_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarImage src={student.picture} />
                      <AvatarFallback className="bg-[#6697a4] text-white text-sm">{student.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <Link to={`/professor/students/${student.user_id}`} className="font-medium text-[#3d5a63] hover:text-[#6697a4] underline decoration-[#6697a4]/30 underline-offset-2 decoration-1 truncate block">{student.name || student.email}</Link>
                      <p className="text-xs text-[#94A3B8] truncate">{student.email}</p>
                      {student.phone && <p className="text-xs text-[#94A3B8] flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" />{student.phone}</p>}
                      <div className="flex gap-1.5 mt-1.5 flex-wrap">
                        {student.ranking_surf && <Badge className={`text-xs border ${RANKING_COLORS[student.ranking_surf] || ""}`}>Surf: {student.ranking_surf}</Badge>}
                        {student.ranking_bodyboard && <Badge className={`text-xs border ${RANKING_COLORS[student.ranking_bodyboard] || ""}`}>BB: {student.ranking_bodyboard}</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 ml-[52px]">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="font-semibold text-[#6697a4]">{student.credits || 0} <span className="text-xs font-normal text-[#94A3B8]">cred.</span></span>
                      <span className="font-semibold text-[#3d5a63]">{student.enrollment_count} <span className="text-xs font-normal text-[#94A3B8]">aulas</span></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => onOpenRankingDialog(student)} data-testid={`prof-ranking-${student.user_id}`}>
                        <Star className="h-3.5 w-3.5 mr-1" /> Rankings
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2 bg-[#6697a4]/5 border-[#6697a4]/30 text-[#6697a4] hover:bg-[#6697a4]/10" onClick={() => onOpenFeedbackDialog(student)} data-testid={`prof-feedback-btn-${student.user_id}`}>
                        <MessageCircle className="h-3.5 w-3.5 mr-1" /> Feedback
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2" data-testid="prof-students-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="h-8 px-3" data-testid="prof-students-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
                  let p;
                  if (pages <= 5) { p = i + 1; }
                  else if (page <= 3) { p = i + 1; }
                  else if (page >= pages - 2) { p = pages - 4 + i; }
                  else { p = page - 2 + i; }
                  return (
                    <Button key={p} variant={p === page ? "default" : "outline"} size="sm" className={`h-8 w-8 p-0 ${p === page ? "bg-[#6697a4] text-white" : ""}`} onClick={() => onPageChange(p)}>{p}</Button>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPageChange(page + 1)} className="h-8 px-3" data-testid="prof-students-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProfessorStudents;
